<!--
  The page shown when the session can't load. For an outage (changelist's
  server not answering or failing, GitHub behind it, or the browser
  offline) it says so in plain words, says what is safe, and tries again
  on its own until a try loads the session, which renders the app in
  place. Only one tab tries (see logic/reconnect.ts); the others follow
  it. The leader waits a random time up to 2 s, 4 s, 8 s, then 15 s between
  tries, tries at once when the browser comes back online, and when the
  tab is shown again only if a try is due. Any other failure gets its
  explanation and Try again. Both offer the operator's support link when
  one is configured, and the request behind a collapsed Details
  disclosure.

  Screen readers hear the headline once (an alert), then how it stands at
  most once every 30 s, never the ticking countdown; App says when
  changelist is back.
-->
<script lang="ts">
  import { untrack } from 'svelte';
  import { explain, isTransient, type Failure } from '../api/failure';
  import { supportLink } from '../logic/operatorLinks';
  import { Retrier, type RetryState } from '../logic/backoff';
  import { coordinateReconnect } from '../logic/reconnect';
  import { session } from '../state/session.svelte';
  import FailureDetails from './FailureDetails.svelte';

  let { failure }: { failure: Failure } = $props();

  const outage = $derived(isTransient(failure) || failure.cause === 'server_error');
  const HEADLINES: Partial<Record<Failure['cause'], string>> = {
    unreachable: "Changeling can't reach its server right now.",
    offline: "You're offline.",
    server_error: "Changeling's server isn't working right now.",
    github_slow: "Changeling can't reach GitHub right now.",
    github_error: "Changeling can't reach GitHub right now.",
  };
  const headline = $derived(HEADLINES[failure.cause] ?? "Changeling couldn't start.");
  const words = $derived(explain(failure, 'your session'));
  const support = $derived(supportLink(session.operatorLinks));

  let role = $state<'leader' | 'follower'>('leader');
  let retry = $state<RetryState>({ phase: 'trying' });
  let now = $state(Date.now());
  const seconds = $derived(retry.phase === 'waiting' ? Math.max(1, Math.ceil((retry.at - now) / 1000)) : 0);
  /**
   * What the live region says. The headline (an alert) speaks the first
   * failure and App speaks the recovery; while the outage lasts this says
   * how it stands at most once every ANNOUNCE_EVERY_MS.
   */
  let announcement = $state('');
  const ANNOUNCE_EVERY_MS = 30_000;
  let lastSpoken = Date.now();
  function announce(text: string) {
    if (Date.now() - lastSpoken < ANNOUNCE_EVERY_MS) return;
    lastSpoken = Date.now();
    announcement = text;
  }
  let retrier: Retrier | null = null;
  let tryOnce: (() => void) | null = null;
  const retryNow = () => (retrier ? retrier.retryNow() : tryOnce?.());

  /** One try: true once the session loaded (the page then leaves on its own). */
  async function attempt(): Promise<boolean> {
    await session.load();
    return session.status !== 'error';
  }

  $effect(() => {
    if (!outage) return;
    // Set up once per outage: what the callbacks read must not re-run it.
    return untrack(start);
  });

  function start() {
    let stopped = false;
    let stopLeading = () => {};
    let following = false;
    tryOnce = () => {
      if (following) return;
      following = true;
      void attempt().finally(() => (following = false));
    };
    const coordination = coordinateReconnect({
      onRole: (r) => (role = r),
      // Another tab got through: this one loads too.
      onUp: () => tryOnce?.(),
      lead: () =>
        new Promise<void>((resolve) => {
          if (stopped) return resolve();
          const r = new Retrier(
            async () => {
              const ok = await attempt();
              if (ok) coordination.recovered();
              return ok;
            },
            (s) => {
              const failed = retry.phase === 'trying' && s.phase === 'waiting';
              retry = s;
              now = Date.now();
              if (failed) announce(`Still can't reach it. Trying again in ${Math.ceil((s.at - now) / 1000)} seconds.`);
              if (s.phase === 'done') resolve();
            },
          );
          retrier = r;
          r.start();
          stopLeading = () => {
            r.stop();
            retrier = null;
            resolve();
          };
        }),
    });
    const tick = setInterval(() => (now = Date.now()), 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') retrier?.retryIfDue();
    };
    const onOnline = () => retrier?.retryNow();
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      stopLeading();
      coordination.stop();
      tryOnce = null;
      clearInterval(tick);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }
</script>

<div class="center-msg unreachable">
  <h1 role="alert">{headline}</h1>
  {#if outage}
    <p>
      Review marks you set are saved in this browser and will be sent when it's back. Comments you were writing are kept
      here too; send them again once it's back.
    </p>
    <p class="retry-line">
      <span class="muted">
        {#if role === 'follower'}
          Another Changeling tab is checking; this one follows it.
        {:else if retry.phase === 'waiting'}
          Trying again in {seconds} s…
        {:else}
          Trying again…
        {/if}
      </span>
      <button class="btn" type="button" onclick={retryNow}>Retry now</button>
    </p>
    <p class="sr-only" role="status">{announcement}</p>
  {:else}
    <p>
      {words.summary}{#if words.advice}{' '}<span class="muted">{words.advice}</span>{/if}
    </p>
    <button class="btn" type="button" onclick={() => session.load()}>Try again</button>
  {/if}
  {#if support}
    <p>
      <a href={support.href} target={support.newTab ? '_blank' : undefined} rel="noreferrer" data-external>Contact support</a>
      <span class="muted">if it keeps happening, with the details below.</span>
    </p>
  {/if}
  <FailureDetails {failure} />
</div>

<style>
  .unreachable {
    max-width: 620px;
  }
  .unreachable h1 {
    font-size: clamp(26px, 3.4vw, 40px);
  }
  .retry-line {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 14px;
    align-items: center;
    justify-content: center;
  }
  .unreachable :global(.failure-details) {
    width: 100%;
  }
</style>
